import { useMemo, useCallback } from 'react';
import {
  useWbsClientLabelSearchLazyQuery,
  useWbsProjectLabelSearchLazyQuery,
  useWbsTaskLabelSearchLazyQuery,
  WbsClientLabelSearchQuery,
  WbsProjectLabelSearchQuery,
  WbsTaskLabelSearchQuery
} from 'graphql/types';
import { useNoteContext } from 'modules/notes/NoteContext';
import { CPT_LABEL_PAGE_COUNT } from '../components/constants';
import { CPTVariantType } from '../components/schema';

export type LabelSearchParams = {
  context: CPTVariantType;
  query?: string | undefined | null;
  clientUri?: string | null | undefined;
  projectUri?: string | null | undefined;
  programUri?: string | null | undefined;
};
export type CPTLabelSearchType = {
  data: {
    client: WbsClientLabelSearchQuery | undefined;
    project: WbsProjectLabelSearchQuery | undefined;
    task: WbsTaskLabelSearchQuery | undefined;
  };
  loading: { client: boolean; project: boolean; task: boolean };
  searchHandler: (searchParams: LabelSearchParams) => void;
};

export const useCPTLabelSearch = (): CPTLabelSearchType => {
  const { date } = useNoteContext();

  const [
    getClients,
    { data: clientsData, loading: clientsLoading }
  ] = useWbsClientLabelSearchLazyQuery();
  const [
    getProjects,
    { data: projectsData, loading: projectsLoading }
  ] = useWbsProjectLabelSearchLazyQuery();

  const [
    getTasks,
    { data: tasksData, loading: tasksLoading }
  ] = useWbsTaskLabelSearchLazyQuery();

  const searchHandler = useCallback(
    (searchParams: LabelSearchParams) => {
      const variables = {
        date: date.toISODate(),
        input: {
          count: CPT_LABEL_PAGE_COUNT,
          query: searchParams.query,
          filter: {
            client: searchParams.clientUri,
            program: searchParams.programUri,
            project: searchParams.projectUri
          }
        }
      };
      if (searchParams.context === 'client') getClients({ variables });
      if (searchParams.context === 'project') getProjects({ variables });
      if (searchParams.context === 'task') getTasks({ variables });
    },
    [date, getClients, getProjects, getTasks]
  );

  return useMemo(
    () => ({
      searchHandler,
      loading: {
        client: clientsLoading,
        project: projectsLoading,
        task: tasksLoading
      },
      data: { client: clientsData, project: projectsData, task: tasksData }
    }),
    [
      clientsData,
      clientsLoading,
      projectsData,
      projectsLoading,
      tasksData,
      tasksLoading,
      searchHandler
    ]
  );
};

export default useCPTLabelSearch;
